import { Alert, Button, Divider, Input, Modal } from "antd";
import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import "./course-registration.scss";
import "../../shared/CBShared.style.scss";
import { rowInvoice } from "./course-registration-step2";
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { CourseRegistrationDTO } from "../../../interfaces/course-registration.interface";
import CustomCard from "../../shared/CustomCard/CustomCard";
import PayPalPayment from "../../paypal/paypal-payment";
import { CBHandMessage } from "../../shared/CBHandMessage/CBHandMessage";
import { useDimensions } from "../../../hooks/useDimensions";

interface Props {
  postRegistration: (payMethod: string) => void;
  setSummary: (payMethod: string, link: string) => void;
  changeStep: (step: number) => void;
  data: CourseRegistrationDTO;
  partial?: boolean;
}

export default function CourseRegistrationStep3(props: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { width } = useDimensions();
  const [modal, contextHolderModal] = Modal.useModal();

  const getGST = () => {
    const { course } = props.data;
    if (course?.gst?.length && course?.gst[0]) {
      const percentage = Number(course?.gst[0].value) / 100;
      return parseFloat((percentage * Number(course.subtotalPrice)).toFixed(2));
    }
    return 0;
  };

  const getTotal = () => {
    const { course } = props.data;
    return (Number(course?.subtotalPrice) + getGST()).toFixed(2);
  };

  const confirm = () => {
    modal.confirm({
      title: "Confirm cancel registration",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to cancel the registration for this course?",
      okText: "Ok",
      cancelText: "Cancel",
      onOk: () => {
        navigate(`/SEABC/courses/${id}`);
      },
    });
  };

  const switchStep = (step: number) => {
    props.changeStep(step);
  };

  const endProcess = () => {
    confirm();
  };

  const postRegistration = (payMethod: string) => {
    props.postRegistration(payMethod);
  };

  return (
    <div
      className={`flex flex-col justify-center items-center registration-registration-step ${!props?.partial ? "p-10" : undefined}`}
    >
      {!props.partial && (
        <CBHandMessage color={"yellow"}>
          Review your invoice and select your payment option
        </CBHandMessage>
      )}
      <div
        className={`flex flex-col gap-5 mt-10 ${!props?.partial ? "pb-10" : undefined}`}
        style={{ width: width >= 500 ? "40rem" : "25rem" }}
      >
        <CustomCard title="My order invoice:">
          <div
            className={`flex pb-3 ${width >= 500 ? "gap-10" : "flex-wrap gap-2"}`}
          >
            {section("First Name:", props.data.firstName)}
            {section("Last Name:", props.data.lastName)}
          </div>
          <div
            className={`flex pb-3 ${width >= 500 ? "gap-10" : "flex-wrap gap-2"}`}
          >
            {section("Email:", props.data.email)}
            {section("Phone:", props.data.phone)}
          </div>
          <Divider></Divider>
          {rowInvoice(
            false,
            null,
            <Title level={4} className="header-title">
              Price
            </Title>,
            <Title level={4} className="header-title">
              Item
            </Title>,
          )}
          {rowInvoice(
            false,
            <>
              <Text strong>{props.data.course?.name}</Text>
              <br />
              <Text italic>
                Registration type: {props.data.course?.registrationType}
              </Text>
            </>,
            <Text strong>${Number(props.data.course?.price).toFixed(2)}</Text>,
            <Text type="warning">{props.data.course?.code}</Text>,
          )}
          {Number(props.data.course?.discountBeforeDueDate) > 0 &&
            rowInvoice(
              true,
              <Text>EarlyBird Discount</Text>,
              <Text strong style={{ color: "green" }}>
                - ${Number(props.data.course?.discountBeforeDueDate).toFixed(2)}
              </Text>,
            )}
          {props?.data?.licensePurchase &&
            props?.data?.course?.license?.length &&
            props?.data?.course?.license[0]?.enable &&
            rowInvoice(
              true,
              <Text>License</Text>,
              <Text strong>
                ${Number(props.data.course?.license[0]?.value).toFixed(2)}
              </Text>,
            )}
          {props.data.member === "1" &&
            rowInvoice(
              true,
              <Text>Member Discount</Text>,
              <Text strong type="success" style={{ color: "green" }}>
                - ${Number(props.data.course?.memberDiscount).toFixed(2)}
              </Text>,
            )}
          {rowInvoice(
            false,
            <Text strong>Subtotal</Text>,
            <Text strong>
              ${Number(props.data.course?.subtotalPrice).toFixed(2)}
            </Text>,
          )}
          {props?.data?.course?.gst?.length &&
            props?.data?.course?.gst[0]?.enable &&
            rowInvoice(
              true,
              <Text>{`GST (${props.data.course?.gst[0].value}%)`}</Text>,
              <Text strong>${getGST()}</Text>,
            )}
          {Number(props.data.totalFees) > 0 &&
            rowInvoice(
              true,
              <Text>Total Fees</Text>,
              <Text strong>${props.data.totalFees ?? "0.00"}</Text>,
            )}
          {rowInvoice(
            false,
            <Title className="my-0.5" level={4}>
              Total
            </Title>,
            <Title style={{ color: "#29abe2" }} className="my-0.5" level={5}>
              ${getTotal()}
            </Title>,
          )}
          {props.data.balance && props.data.balance !== "0.00" && (
            <div>
              <Alert
                message={
                  <Title level={4}>
                    You previously paid: $ {props.data.amountPayed}
                  </Title>
                }
                type="warning"
                className="mt-4"
              />
              <Alert
                message={
                  <Title level={4}>
                    You have a balance owing of: $ {props.data.balance}
                  </Title>
                }
                type="error"
                className="mt-4"
              />
            </div>
          )}
        </CustomCard>
        {!props.partial && (
          <>
            <CustomCard title="Please select your payment option:">
              <div className="flex justify-center gap-10 py-3">
                <PayPalPayment
                  data={props.data}
                  setSummary={(payMethod: string, link: string) =>
                    props.setSummary(payMethod, link)
                  }
                />
                {props.data.paymentMethod !== "Cheque" &&
                  props.data.paymentMethod !== "Paypal" &&
                  props.data.balance !== "0.00" && (
                    <div>
                      <Title
                        onClick={() => postRegistration("Cheque")}
                        className="cursor-pointer mt-2 underline cb-text-blue"
                        level={4}
                      >
                        Pay by Cheque
                      </Title>
                    </div>
                  )}
              </div>
            </CustomCard>
            <div className="flex justify-center gap-8 pt-10">
              {props.data.paymentMethod !== "Cheque" &&
                !props.data.waitList &&
                props.data.balance !== "0.00" && (
                  <Button
                    className="h-9"
                    htmlType="button"
                    icon={<RollbackOutlined />}
                    onClick={() => switchStep(-1)}
                  >
                    Go Back
                  </Button>
                )}

              <Button
                className="h-9"
                type="primary"
                htmlType="button"
                icon={<CloseOutlined />}
                onClick={endProcess}
                danger
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
      {contextHolderModal}
    </div>
  );
}

const section = (title: string, value: string) => {
  return (
    <div style={{ minWidth: "15rem" }}>
      <Text>{title}</Text>
      <Input value={value} disabled />
    </div>
  );
};
